import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import HeroHeader from "./HeroHeader";
import CategoryTabs from "./CategoryTabs";
import TeamSection from "./TeamSection";
import ParticleBackground from "./ParticleBackground";

// Team data organized by section
const teamData = {
  "Board Members": {
    id: "board-members",
    icon: "🏆",
    members: [
      { name: "Akilesh T", role: "President", image: "/Members/president/akilesh.png" },
      { name: "Ghayathri S V", role: "Vice President"},
    ],
  },
  "Treasurer": {
    id: "treasurer",
    icon: "💼",
    members: [
      { name: "Abhishek G Shetty", role: "Coordinator", image: "/Members/traseurer/abhishek1.png" },
      { name: "Naveen Raj V", role: "Coordinator", image: "/Members/traseurer/naveen1.png" },
      { name: "Dharshini K", role: "Coordinator" },
    ],
  },
  "Technical Team": {
    id: "technical-team",
    icon: "💻",
    members: [
      { name: "Assun E", role: "Technical Lead" },
      { name: "Sherlin S", role: "Technical Lead" },
    ],
  },
  "Non Technical Team": {
    id: "non-technical-team",
    icon: "🧠",
    members: [
      { name: "Ajay A", role: "Non Technical Lead" },
      { name: "Jeevanesan G", role: "Non Technical Lead" },
    ],
  },
  "Sponsors Team": {
    id: "sponsors-team",
    icon: "📢",
    members: [
      { name: "Rooshitha Jemimah R", role: "Lead" },
      { name: "Rahul Kanth S", role: "Member" },
    ],
  },
  "Fund Raiser": {
    id: "fund-raiser",
    icon: "💰",
    members: [
      { name: "Midarshana", role: "Coordinator" },
      { name: "Praveen Kumar", role: "Coordinator" },
    ],
  },
  
  "Junior Coordinators": {
    id: "junior-coordinators",
    icon: "🌱",
    members: [
      { name: "Vijaykumar R", role: "Coordinator" },
      { name: "Yuvasree I", role: "Coordinator" },
      { name: "Jayasree R", role: "Coordinator" },
    ],
  },
  "Social Media Coordinators": {
    id: "social-media-coordinators",
    icon: "📱",
    members: [
      { name: "Dhanush R", role: "Manager" },
      { name: "Yokesh J", role: "Manager" },
      { name: "Balakumaran M", role: "Manager" },
    ],
  },
  "Design Team": {
    id: "design-team",
    icon: "🎨",
    members: [
      { name: "Naveen R", role: "Member" },
      { name: "Raasika C", role: "Member" },
      { name: "Sonika E", role: "Member" },
    ],
  },
  
  "Web Developers - Cyber Bullies": {
    id: "web-developers",
    icon: "🌐",
    members: [
      { name: "Bhuvaneshwar kumar S S", role: "Member" },
      { name: "Gokul Krishnan V", role: "Member" },
      { name: "Manikandan N", role: "Member" },
      { name: "Pranesh", role: "Member" },
    ],
  },
};

const categories = ["ALL", "COMMAND", "CREATIVE", "TECHNICAL", "NON TECHNICAL TEAM", "SPECIAL"];

const categoryMapping: Record<string, string[]> = {
  "ALL": Object.keys(teamData),
  "COMMAND": ["Board Members", "Treasurer", "Fund Raiser", "Junior Coordinators"],
  "CREATIVE": ["Design Team", "Sponsors Team", "Social Media Coordinators"],
  "TECHNICAL": ["Technical Team", "Web Developers - Cyber Bullies"],
  "NON TECHNICAL TEAM": ["Non Technical Team"],
  "SPECIAL": ["Special Thanks"],
};

const Index = () => {
  const [activeCategory, setActiveCategory] = useState("ALL");

  const filteredSections = useMemo(() => {
    const sectionNames = categoryMapping[activeCategory] || Object.keys(teamData);
    return sectionNames.map((name) => ({
      title: name,
      ...teamData[name as keyof typeof teamData],
    }));
  }, [activeCategory]);

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      {/* Particle Background */}
      <ParticleBackground />

      {/* Background grid pattern */}
      <div className="fixed inset-0 bg-grid-pattern opacity-20 pointer-events-none" />
      
      {/* Floating background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ 
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            x: [0, -80, 0],
            y: [0, 80, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-primary/3 rounded-full blur-3xl"
        />
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full min-h-screen flex flex-col">
        {/* Header Section - Centered */}
        <section className="w-full flex justify-center items-center py-8 md:py-12">
          <div className="w-full max-w-7xl">
            <HeroHeader />
          </div>
        </section>

        {/* Category Tabs - Centered */}
        <section className="w-full flex justify-center items-center py-4 md:py-6">
          <div className="w-full max-w-7xl px-4">
            <CategoryTabs
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>
        </section>

        {/* Team Sections - Centered and Aligned */}
        <main className="flex-1 w-full">
          <div className="w-full max-w-7xl mx-auto px-4 pb-20">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full"
            >
              {filteredSections.map((section) => (
                <div key={section.id} className="w-full mb-12 last:mb-0">
                  <TeamSection
                    id={section.id}
                    title={section.title}
                    icon={section.icon}
                    members={section.members}
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </main>

        {/* Footer Spacing */}
        <div className="h-8" />
      </div>
    </div>
  );
};

export default Index;